/**
 * ASCII движок для визуализации CSElement структур
 */

import {
  VisualizationEngine,
  VisualizationGraph,
  VisualizationOptions,
  VisualizationResult,
  VisualizationFormat,
  VisualizationNode,
  TreeDirection
} from '../../types/visualization-interfaces';

export class AsciiEngine implements VisualizationEngine {
  name = 'ascii';
  supportedFormats: VisualizationFormat[] = ['ascii'];

  /**
   * Проверка поддержки формата
   */
  supportsFormat(format: VisualizationFormat): boolean {
    return this.supportedFormats.includes(format);
  }

  /**
   * Рендеринг графа в ASCII формат
   */
  async render(
    graph: VisualizationGraph,
    options: VisualizationOptions
  ): Promise<VisualizationResult> {
    const startTime = Date.now();
    
    const {
      direction = 'top-bottom',
      title,
      showLegend = false,
      maxDepth = Infinity
    } = options;

    // Строим дерево из графа
    const tree = this.buildTree(graph);
    
    // Генерируем ASCII представление
    const lines: string[] = [];
    
    // Добавляем заголовок
    if (title) {
      lines.push(title);
      lines.push('='.repeat(title.length));
      lines.push('');
    }

    // Рендерим дерево
    const treeLines = this.renderTree(tree, direction, maxDepth);
    lines.push(...treeLines);

    // Добавляем легенду
    if (showLegend) {
      lines.push('');
      lines.push('Легенда:');
      lines.push('├── Дочерний элемент');
      lines.push('└── Последний дочерний элемент');
      lines.push('│   Продолжение ветки');
      lines.push('    Пустое место');
    }

    const content = lines.join('\n');
    
    return {
      content,
      format: 'ascii',
      metadata: {
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        maxDepth: graph.metadata.maxDepth,
        generationTime: Date.now() - startTime,
        dimensions: {
          width: Math.max(...lines.map(line => line.length)),
          height: lines.length
        }
      }
    };
  }

  /**
   * Построение дерева из графа
   */
  private buildTree(graph: VisualizationGraph): TreeNode {
    const nodeMap = new Map<string, VisualizationNode>();
    const childrenMap = new Map<string, string[]>();

    // Индексируем узлы
    graph.nodes.forEach(node => {
      nodeMap.set(node.id, node);
      childrenMap.set(node.id, []);
    });

    // Строим связи родитель-дети
    graph.edges.forEach(edge => {
      if (edge.type === 'parent-child') {
        const children = childrenMap.get(edge.source) || [];
        children.push(edge.target);
        childrenMap.set(edge.source, children);
      }
    });

    // Находим корневой узел
    const rootNode = nodeMap.get(graph.metadata.rootId);
    if (!rootNode) {
      throw new Error('Корневой узел не найден');
    }

    // Рекурсивно строим дерево
    const buildNode = (nodeId: string): TreeNode => {
      const node = nodeMap.get(nodeId)!;
      const childIds = childrenMap.get(nodeId) || [];
      
      return {
        node,
        children: childIds.map(childId => buildNode(childId))
      };
    };

    return buildNode(graph.metadata.rootId);
  }

  /**
   * Рендеринг дерева в ASCII
   */
  private renderTree(
    tree: TreeNode,
    _direction: TreeDirection,
    maxDepth: number
  ): string[] {
    const lines: string[] = [];

    const renderNode = (
      node: TreeNode,
      prefix: string = '',
      isLast: boolean = true,
      depth: number = 0
    ): void => {
      if (depth > maxDepth) {
        return;
      }

      // Создаем префикс для текущего узла
      const connector = isLast ? '└── ' : '├── ';
      const nodePrefix = depth === 0 ? '' : prefix + connector;
      
      // Добавляем узел
      lines.push(nodePrefix + this.formatNodeLabel(node.node));

      // Рендерим дочерние узлы
      if (node.children.length > 0 && depth < maxDepth) {
        const childPrefix = depth === 0 ? '' : prefix + (isLast ? '    ' : '│   ');
        
        node.children.forEach((child, index) => {
          const isLastChild = index === node.children.length - 1;
          renderNode(child, childPrefix, isLastChild, depth + 1);
        });
      }
    };

    renderNode(tree);
    return lines;
  }

  /**
   * Форматирование метки узла
   */
  private formatNodeLabel(node: VisualizationNode): string {
    const parts: string[] = [node.label];

    // Добавляем дополнительную информацию
    if (node.data) {
      const info: string[] = [];
      
      if (node.data.childrenCount > 0) {
        info.push(`${node.data.childrenCount} детей`);
      }
      
      if (node.data.dataSize > 0) {
        info.push(`${node.data.dataSize} данных`);
      }

      if (info.length > 0) {
        parts.push(`(${info.join(', ')})`);
      }
    }

    return parts.join(' ');
  }
}

/**
 * Узел дерева для ASCII рендеринга
 */
interface TreeNode {
  node: VisualizationNode;
  children: TreeNode[];
} 